<script setup>
// Popup do "percurso do cliente" — renderiza o flow em modo leitura (Vue Flow) destacando
// o nó atual da execução. Apenas visual, para o usuário ver onde o cliente está.
import { onMounted, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import FlowCustomNode from '../FlowCustomNode.vue';

const props = defineProps({
  flowName: { type: String, default: '' },
  flowData: { type: Object, default: () => ({}) },
  currentNodeId: { type: String, default: null },
});
const emit = defineEmits(['close']);
const { t } = useI18n();

const nodeTypesMap = { custom: markRaw(FlowCustomNode) };
const { setNodes, setEdges, fitView } = useVueFlow('journey');

onMounted(() => {
  const fd = props.flowData || {};
  const raw = fd.nodes || [];
  // alguns flows (ex.: sequências antigas) não têm posições salvas → nós ficariam empilhados.
  // Detecta posições ausentes/iguais e aplica um layout vertical simples para ficar legível.
  const distinct = new Set(raw.map(n => `${n.position?.x},${n.position?.y}`));
  const needsLayout = raw.some(n => !n.position) || distinct.size <= 1;
  const nodes = raw.map((n, i) => ({
    id: n.id,
    type: 'custom',
    position: needsLayout ? { x: 0, y: i * 130 } : n.position,
    data: {
      ...n.data,
      isCurrent: n.id === props.currentNodeId,
      onDelete: undefined,
    },
  }));
  setNodes(nodes);
  setEdges(
    (fd.edges || []).map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      animated: true,
    }))
  );
  // espera o VueFlow medir os nós antes de enquadrar todo o flow
  setTimeout(() => fitView({ padding: 0.2, maxZoom: 1 }), 280);
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
    @click.self="emit('close')"
  >
    <div
      class="w-[56rem] max-w-full h-[80vh] flex flex-col bg-n-background rounded-xl shadow-xl overflow-hidden"
    >
      <div
        class="flex items-center justify-between px-5 py-3 border-b border-n-weak shrink-0"
      >
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('CHATBOT.journey.title') }} — {{ flowName }}
        </h2>
        <button
          class="text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <svg
            class="w-5 h-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="flex-1 min-h-0 bg-n-alpha-1">
        <VueFlow
          id="journey"
          :node-types="nodeTypesMap"
          :nodes-draggable="false"
          :nodes-connectable="false"
          :elements-selectable="false"
          :max-zoom="1.2"
          :min-zoom="0.2"
        >
          <Background
            :gap="16"
            :size="1"
            pattern-color="var(--n-weak, #cbd5e1)"
          />
          <Controls :show-interactive="false" />
        </VueFlow>
      </div>
    </div>
  </div>
</template>
